import { ShareEditability } from './shareLifecycle';

export type SharedMembershipUpdateRestriction = {
    title: string;
    message: string;
};

type SharedMembershipUpdateRestrictionParams = {
    source: 'pt' | 'group';
    isActiveSharePool: boolean;
    /**
     * `resolveShareEditability()` 결과. 서버 판정(`shareLifecycle`)이 없으면 폴백이 이미
     * 적용돼 있다. 예전의 `isTemplateIssued: sharePolicySnapshot?.enabled === true` 추론을
     * 대신한다 — 그 추론이 "공유 상품으로 발급됐다"(영구 사실)를 "지금 막아야 한다"로
     * 읽어 종료 뒤에도 영구히 잠그던 원인이었다.
     */
    editability: ShareEditability;
};

/**
 * 공유 중 **전면** 차단 문구. 서버가 `editableWhileSharedFields` 를 안 내려주는
 * 구버전에서만 쓴다 — 그 서버는 실제로 이 화면이 보내는 모든 필드를 거절한다.
 *
 * 여기서 말하는 "기간"은 **이용 기간**(시작·만료 날짜)이다. 폼의 `period` 는 수업
 * 시간(분)이라 이름이 겹치지만 다른 값이고, 그쪽은 공유 중에도 서버가 허용한다.
 */
const ACTIVE_SHARE_RESTRICTION: SharedMembershipUpdateRestriction = {
    title: '공유 중에는 수정할 수 없어요',
    message:
        '회차·이용 기간·금액 등 수강권 정보를 바꾸려면 공유를 먼저 종료해주세요. 수정한 뒤에는 같은 수강권으로 다시 공유를 시작할 수 있어요.',
};

/**
 * 구버전 서버(=`shareLifecycle` 없음)에서 공유 상품 발급 건에 띄우는 문구.
 *
 * 그 서버에서는 공유를 종료해도 계속 막히므로 **"종료하면 열린다"고 약속하면 안 된다.**
 * 공유 종료는 되돌릴 수 없는 동작이라, 거짓 약속을 보고 종료했다가 그대로 갇힌다.
 */
const TEMPLATE_SHARE_RESTRICTION: SharedMembershipUpdateRestriction = {
    title: '공유 상품 수강권은 수정할 수 없어요',
    message:
        '공유 상품으로 발급된 수강권의 회차·기간·금액 등은 일반 수정할 수 없어요. 회차나 금액 변경이 필요하면 환불 또는 업그레이드를 이용해주세요.',
};

/**
 * 서버가 판정을 내려줬는데도 수정이 닫힌 경우 — 발급 진행 중(PROVISIONING)이거나
 * 기록이 어긋난 상태(INCONSISTENT)다. 공유 종료로 풀리는 문제가 아니라서
 * ACTIVE·구버전과 다른 문구를 쓴다. 단계 이름을 화면에 그대로 노출하지는 않는다.
 */
const TEMPLATE_SHARE_PENDING_RESTRICTION: SharedMembershipUpdateRestriction = {
    title: '지금은 수정할 수 없어요',
    message:
        '공유 상품 발급 처리가 끝나지 않았거나 공유 기록에 확인이 필요한 상태예요. 위 공유 수강권 카드에서 설정 상태를 확인한 뒤 다시 시도해주세요.',
};

/**
 * 공유 **중**에 이 화면이 열어 줄 수 있는 입력 — **폼이 가진 것 전부**.
 *
 * 실제로 여는 집합은 **이 목록과 서버 `editableWhileSharedFields` 의 교집합**이다.
 * 프론트는 여기서 더 좁히지 않는다. 서버가 목록을 넓히면 화면이 자동으로 따라 열린다.
 *
 * 목록에서 빠진 것은 "잠갔다"가 아니라 **이 모달에 입력이 없다**는 뜻이다.
 *   - `name` — 이름 수정 버튼이 주석 처리돼 있다(별건 티켓).
 *   - `BGColor` — 표시 전용이고 수정 API 본문에도 실리지 않는다.
 *   - `isSplitPayment` — 폼이 값을 읽기만 하고 바꾸는 입력이 없다.
 * 없는 입력을 넣으면 안내 문구가 화면에 존재하지 않는 것을 약속하게 된다.
 *
 * 순서는 **화면에 나오는 순서**다. 안내 문구가 이 순서로 나열된다.
 */
export const SHARED_ACTIVE_EDITABLE_FORM_FIELDS = [
    'pricePerSchedule',
    'totalPrice',
    'activationDate',
    'expirationDate',
    'startSessionValue',
    'totalSession',
    'period',
    'expiryPolicy',
    'salesManager',
    'isNewSales',
    'initialPaymentDate',
    'memo',
] as const;

export type SharedActiveEditableFormField = (typeof SHARED_ACTIVE_EDITABLE_FORM_FIELDS)[number];

/** 안내 문구에 쓰는 이름. 화면의 입력 라벨과 **글자 그대로** 같아야 찾을 수 있다. */
const SHARED_ACTIVE_EDITABLE_FIELD_LABELS: Record<SharedActiveEditableFormField, string> = {
    pricePerSchedule: '회당 가격',
    totalPrice: '판매 금액',
    activationDate: '시작 날짜',
    expirationDate: '만료 날짜',
    startSessionValue: '시작 회차',
    totalSession: '총 회차',
    period: '수업 시간',
    expiryPolicy: '만료 정책',
    salesManager: '판매담당자',
    isNewSales: '회원 구분',
    initialPaymentDate: '결제일자',
    memo: '결제 메모',
};

const labelsOf = (fields: readonly SharedActiveEditableFormField[]): string =>
    fields.map((field) => SHARED_ACTIVE_EDITABLE_FIELD_LABELS[field]).join('·');

/**
 * 공유 중 부분 편집 안내.
 *
 * **잠긴 목록을 고정 문자열로 쓰지 않는다.** 서버가 허용 범위를 넓히면 잠긴 것이 줄어드는데,
 * 고정 문구를 두면 실제로는 열려 있는 입력을 "잠겼다"고 계속 말하게 된다. 열린 목록의
 * 여집합으로 매번 만든다.
 */
const buildActiveShareLimitedNotice = (
    editableFields: SharedActiveEditableFormField[],
): SharedMembershipUpdateRestriction => {
    const editable = new Set<string>(editableFields);
    const lockedFields = SHARED_ACTIVE_EDITABLE_FORM_FIELDS.filter(
        (field) => !editable.has(field),
    );
    if (lockedFields.length === 0) {
        return {
            title: '공유 중에도 수정할 수 있어요',
            message:
                '바꾼 값은 이 수강권을 함께 쓰는 참여자에게도 그대로 적용돼요. 이미 사용·예약된 회차 아래로는 총 회차를 줄일 수 없어요.',
        };
    }
    /**
     * **짧은 쪽을 나열한다.**
     *
     * 서버가 허용 범위를 넓히면서 상황이 뒤집혔다. 예전에는 열린 게 하나·잠긴 게 열한 개라
     * 열린 쪽을 말했는데, 지금은 반대로 **열린 게 열한 개·잠긴 게 하나**다. 그대로 두면
     * 열한 개를 나열해 놓고 정작 관리자가 찾는 **"내가 누른 이 입력이 왜 회색인지"** 는
     * "나머지"로 뭉뚱그린다. 목록은 짧아야 읽힌다.
     *
     * 잠긴 쪽을 말할 때는 조사를 붙이지 않는다 — 마지막 항목 받침에 따라 은/는이 갈려
     * "결제일자은" 같은 문장이 나온다.
     */
    if (lockedFields.length <= editableFields.length) {
        return {
            title: '공유 중에는 바꿀 수 없는 항목이 있어요',
            message: `지금 바꿀 수 없는 항목: ${labelsOf(
                lockedFields,
            )}\n공유를 종료한 뒤에 수정할 수 있어요.`,
        };
    }
    return {
        title: '공유 중에는 일부만 수정할 수 있어요',
        message: `지금은 ${labelsOf(
            editableFields,
        )}만 바꿀 수 있어요. 나머지는 공유를 종료한 뒤에 수정할 수 있어요.`,
    };
};

/**
 * 공유 **중** 저장 본문. 서버가 허용한 필드만 남긴다.
 *
 * 잠그는 것만으로는 부족하다 — 서버는 보호 필드가 본문에 **들어 있기만 해도** 409를 낸다
 * (값이 그대로여도 `hasOwnProperty` 로 판정한다). 폼 전체를 보내면 메모 한 줄만 고쳐도
 * 회차·금액이 함께 실려 거절된다. `PATCH /v3/memberships/:id` 는 `$set` 부분 갱신이라
 * 안 보낸 필드는 그대로 남는다.
 *
 * 값이 `undefined` 인 필드는 뺀다. 판매담당자가 지정된 적 없는 수강권에서 키만 실어
 * 보내면 서버 Joi 검증에 걸린다.
 */
export const buildSharedActiveUpdateBody = <
    /**
     * **허용 필드 전부를 가진 객체만 받는다.**
     *
     * 예전엔 `Record<string, unknown>` 이라 호출부가 네 개만 넘겨도 통과했고,
     * 서버가 목록을 넓히면 나머지 값이 요청에서 조용히 빠졌다(화면은 "수정 완료").
     * 이제 `SHARED_ACTIVE_EDITABLE_FORM_FIELDS` 에 필드를 추가하면 **호출부가
     * 그 값을 넘기지 않는 한 컴파일이 실패한다.** 목록과 값이 함께 늘도록 타입이 잡는다.
     */
    T extends Record<SharedActiveEditableFormField, unknown>,
>(
    editableFields: readonly SharedActiveEditableFormField[],
    values: T,
): Partial<T> => {
    const editable = new Set<string>(editableFields);
    const body: Partial<T> = {};
    // 목록을 돌린다. 필드마다 if 를 쓰면 목록만 늘고 구현이 빠지는 자리가 생긴다.
    SHARED_ACTIVE_EDITABLE_FORM_FIELDS.forEach((field) => {
        if (!editable.has(field)) return;
        const key = field as keyof T;
        if (values[key] === undefined) return;
        body[key] = values[key];
    });
    return body;
};

/**
 * 참여자 영향 경고 문구.
 *
 * **어느 필드가 대상인지는 서버 `shareLifecycle.participantImpactFields` 가 정본이다**
 * (계약, 2026-09-01 확정). 프론트 상수로 갖고 있다가 서버 정책이 바뀌면 조용히
 * 어긋나므로 목록을 여기서 만들지 않는다. 정산 축(`settlementImpactFields`)과 겹치지 않는다.
 */
const PARTICIPANT_IMPACT_FIELD_LABELS: Record<string, string> = {
    activationDate: '시작 날짜',
    expirationDate: '만료 날짜',
    expiryPolicy: '만료 정책',
    period: '수업 시간',
};

export type ParticipantImpactChange = {
    field: string;
    label: string;
};

/**
 * 이번 저장에서 **참여자에게 영향이 가는 값을 실제로 건드렸는지** 가려낸다.
 *
 * 비교 기준은 수강권 문서가 아니라 **폼 초기값**이다 — 정산 경고와 같은 이유로,
 * 레거시 레코드에서 폼 기본값과 문서가 어긋나면 아무것도 안 건드려도 경고가 뜬다.
 *
 * 라벨을 모르는 필드는 서버가 새로 넣은 것이다. 이름을 지어내지 않고 필드명을 그대로
 * 보여준다 — 빠뜨리는 것보다 낫다(정산 경고와 같은 관용구).
 */
export const getParticipantImpactChanges = ({
    participantImpactFields,
    changedFields,
}: {
    participantImpactFields: string[] | null | undefined;
    /** 이번에 값이 바뀐 필드 이름들. 판정은 호출부가 하고 여기서는 교집합만 낸다. */
    changedFields: readonly string[];
}): ParticipantImpactChange[] => {
    if (!Array.isArray(participantImpactFields) || participantImpactFields.length === 0) return [];
    const changed = new Set(changedFields);
    const seen = new Set<string>();
    const changes: ParticipantImpactChange[] = [];
    participantImpactFields.forEach((field) => {
        if (typeof field !== 'string' || !field.trim() || seen.has(field)) return;
        seen.add(field);
        if (!changed.has(field)) return;
        changes.push({ field, label: PARTICIPANT_IMPACT_FIELD_LABELS[field] || field });
    });
    return changes;
};

/**
 * 공유 중 참여자 영향 경고.
 *
 * 이용 기간·만료 정책·수업 시간은 소유자만의 값이 아니다. **참여자가 예약할 수 있는
 * 범위와 슬롯 길이**가 함께 움직여서, 이미 잡힌 예약이 범위 밖으로 나갈 수 있다.
 * 소유자 화면만 보고 바꾸면 남의 예약이 조용히 어긋난다.
 */
export const buildSharedActiveParticipantImpactWarning = (
    changedLabels: readonly string[],
): string =>
    `${changedLabels.join('·')}을(를) 바꾸면 이 수강권을 함께 쓰는 참여자의 예약 가능 범위도 같이 움직여요.\n이미 잡힌 예약이 범위 밖으로 나갈 수 있으니 예약 현황을 확인해주세요.`;

/**
 * 공유 **중** 총 회차를 **늘릴** 때의 경고.
 *
 * 차단이 아니다(2026-09-01 방침 변경). 금액도 같은 화면에서 열려 있어 관리자가 회계를
 * 맞출 수 있기 때문이다. 다만 **결제 없이 회차만 느는 것**은 매출 없는 서비스 제공이라
 * 금액을 함께 확인하라고 말한다. 정식 경로(업그레이드)가 있다는 것도 알려준다.
 */
export const buildSharedActiveSessionIncreaseWarning = ({
    fromTotalSession,
    toTotalSession,
}: {
    fromTotalSession: number;
    toTotalSession: number;
}): string =>
    `총 회차를 ${fromTotalSession}회에서 ${toTotalSession}회로 늘립니다.\n결제 없이 회차만 늘어나므로 판매금액·회당 가격도 함께 맞춰주세요.\n결제까지 함께 처리하려면 업그레이드를 이용하는 것이 정식 경로예요.`;

/**
 * 공유 **중** 총 회차를 **줄일** 때의 경고.
 *
 * **회차를 줄여도 금액은 하나도 따라오지 않는다.** 서버도 폼도 회당 가격을 재계산하지
 * 않는다. 15회 100만원을 10회로 줄이면 100만원이 그대로 남는다. 이걸 모르고 넘어가면
 * 나중에 장부가 왜 이런지 아무도 설명하지 못한다.
 *
 * 이제 금액도 같은 화면에서 열려 있으므로 **여기서 바로 고치라고** 안내한다.
 * (예전의 "환불이나 공유 종료를 쓰라"는 안내는 더 이상 맞지 않는다.)
 */
export const buildSharedActiveSessionDecreaseWarning = ({
    fromTotalSession,
    toTotalSession,
    totalPriceText,
    isPriceEditable,
}: {
    fromTotalSession: number;
    toTotalSession: number;
    /** 이미 통화 형식으로 만든 판매금액. 숫자 포맷은 화면 관용구(convertToKRW)를 따른다. */
    totalPriceText: string;
    /** 이번 화면에서 금액 입력이 열려 있는지. 안내할 다음 행동이 갈린다. */
    isPriceEditable: boolean;
}): string =>
    `총 회차를 ${fromTotalSession}회에서 ${toTotalSession}회로 줄입니다.\n판매금액 ${totalPriceText}원과 회당 가격은 그대로 남습니다 — 회차를 줄여도 금액은 정정되지 않아요.\n${
        isPriceEditable
            ? '금액까지 맞추려면 이 화면에서 회당 가격·판매 금액을 함께 수정해주세요.'
            : '금액까지 정정하려면 환불을 쓰거나, 공유를 종료한 뒤 수정해주세요.'
    }`;

/**
 * 공유 중 총 회차 하한 안내. **숫자를 반드시 넣는다.**
 *
 * "공유를 먼저 종료해주세요"가 나빴던 이유는 이유도 없고 되돌릴 수도 없어서였다.
 * 하한은 반대다 — 몇 회가 이미 잡혀 있는지 보여주면 관리자가 무엇을 정리해야
 * 하는지 안다. 숫자는 전부 서버 `sharedUsage` 값이고, 프론트에서 계산하지 않는다.
 */
export const buildSharedMinimumTotalSessionMessage = (usage: {
    usedCount: number;
    futureScheduledCount: number;
    pendingRequestCount: number;
    minimumTotalSession: number;
}): string => {
    const reasons: string[] = [];
    if (usage.usedCount > 0) reasons.push(`이미 사용 ${usage.usedCount}회`);
    if (usage.futureScheduledCount > 0) reasons.push(`예약 ${usage.futureScheduledCount}회`);
    if (usage.pendingRequestCount > 0) reasons.push(`대기 신청 ${usage.pendingRequestCount}건`);
    const reasonText = reasons.length > 0 ? `${reasons.join('·')}이 있어요. ` : '';
    return `${reasonText}총 회차는 ${usage.minimumTotalSession}회 아래로 줄일 수 없어요.\n예약을 취소했다면 화면을 새로고침한 뒤 다시 시도해주세요.`;
};

/**
 * 이 수강권을 지금 어디까지 고칠 수 있는가.
 *
 * 세 상태뿐이다 — 전부(FULL) · 일부(LIMITED) · 전면 차단(BLOCKED). 화면 두 곳
 * (진입 버튼·모달)이 같은 판정을 따로 만들지 않도록 여기 한 곳에서만 정한다.
 *
 * 그룹 수강권은 별도 MemberTicket 수정 계약을 사용하므로 여기서 임의로 막지 않는다.
 *
 * 공유 **중**(활성 풀) 판정은 `canEditGeneralFields` 와 별개 축이다. 서버의
 * `buildTemplateShareEditabilityView` 는 공유 상품이 아닌 수강권에 대해 단계를 보지 않고
 * `canEditGeneralFields: true` 를 돌려주므로, 수동 전환한 공유 수강권이 공유 중일 때는
 * 그 값이 `true` 다. 실제 차단은 서버의 다른 가드가 한다. 여기서 `isActiveSharePool` 을
 * 없애면 공유 중인 수강권의 모든 입력이 열리고, 눌러야만 409가 보이는 자리가 생긴다.
 */
export type SharedMembershipEditScope = {
    /**
     * 서버가 409 `SHARED_MEMBERSHIP_LIFECYCLE_MUTATION_BLOCKED` 를 냈을 때 띄울 문구.
     * **진입 차단 여부와 별개 축이다** — 일부만 열린 상태(LIMITED)에서도 만료·분할결제
     * 같은 다른 저장 경로는 보호 필드를 보내므로 여전히 409 가 날 수 있다.
     */
    lifecycleBlockedNotice: SharedMembershipUpdateRestriction | null;
} & (
    | { mode: 'FULL' }
    | {
          mode: 'LIMITED';
          editableFields: SharedActiveEditableFormField[];
          notice: SharedMembershipUpdateRestriction;
      }
    | { mode: 'BLOCKED'; restriction: SharedMembershipUpdateRestriction }
);

export const resolveSharedMembershipEditScope = ({
    source,
    isActiveSharePool,
    editability,
}: SharedMembershipUpdateRestrictionParams): SharedMembershipEditScope => {
    if (source === 'group') return { mode: 'FULL', lifecycleBlockedNotice: null };
    /**
     * 서버 판정을 못 받은 공유 상품 발급 건은 **활성 풀이어도** 종료를 권하지 않는다.
     *
     * 구버전 서버에서는 공유를 종료해도 수정·재공유가 계속 막힌다. 그런데 공유 종료는
     * 되돌릴 수 없다. 여기서 ACTIVE 안내("공유를 먼저 종료해주세요")를 띄우면 열리지도
     * 않을 종료를 시켜 놓고 갇히게 만든다. 이 분기가 폴백의 핵심이다.
     */
    if (!editability.hasServerJudgement && editability.isTemplateIssued) {
        return {
            mode: 'BLOCKED',
            restriction: TEMPLATE_SHARE_RESTRICTION,
            lifecycleBlockedNotice: TEMPLATE_SHARE_RESTRICTION,
        };
    }
    if (isActiveSharePool) {
        /**
         * 서버가 허용한다고 말한 것만 연다.
         *
         * `editableWhileSharedFields` 는 서버 판정이 없으면 빈 배열이라, 구버전 서버에서는
         * 교집합이 비어 자동으로 예전과 같은 전면 차단이 된다. 폴백을 따로 쓰지 않는다.
         */
        const editableFields = SHARED_ACTIVE_EDITABLE_FORM_FIELDS.filter((field) => {
            if (!editability.editableWhileSharedFields.includes(field)) return false;
            /**
             * 총 회차만 조건이 하나 더 붙는다 — **하한을 모르면 열지 않는다.**
             * 공유 회차 집계는 프론트가 가진 데이터로 재현할 수 없어서, 하한 없이 열면
             * 사용량 아래로 줄이는 요청이 그대로 서버에 도달하고 저장을 눌러야만 409를 본다.
             */
            if (field === 'totalSession') return editability.sharedUsage !== null;
            return true;
        });
        if (editableFields.length === 0) {
            return {
                mode: 'BLOCKED',
                restriction: ACTIVE_SHARE_RESTRICTION,
                lifecycleBlockedNotice: ACTIVE_SHARE_RESTRICTION,
            };
        }
        return {
            mode: 'LIMITED',
            editableFields,
            notice: buildActiveShareLimitedNotice(editableFields),
            lifecycleBlockedNotice: ACTIVE_SHARE_RESTRICTION,
        };
    }
    if (editability.canEditGeneralFields) return { mode: 'FULL', lifecycleBlockedNotice: null };
    return {
        mode: 'BLOCKED',
        restriction: TEMPLATE_SHARE_PENDING_RESTRICTION,
        lifecycleBlockedNotice: TEMPLATE_SHARE_PENDING_RESTRICTION,
    };
};

/**
 * 일반 수정 API가 보호하는 PT 공유 수강권만 선제 안내한다.
 *
 * `resolveSharedMembershipEditScope` 의 얇은 표현이다. 판정을 여기서 다시 만들지
 * 않는다 — 두 벌이 되면 진입 버튼과 모달이 서로 다른 답을 낸다.
 */
export const getSharedMembershipUpdateRestriction = (
    params: SharedMembershipUpdateRestrictionParams,
): SharedMembershipUpdateRestriction | null => {
    const scope = resolveSharedMembershipEditScope(params);
    return scope.mode === 'BLOCKED' ? scope.restriction : null;
};

export const isSharedMembershipLifecycleMutationBlocked = (data: unknown): boolean => {
    if (!data || typeof data !== 'object') return false;
    return (
        (data as { errorCode?: unknown }).errorCode ===
        'SHARED_MEMBERSHIP_LIFECYCLE_MUTATION_BLOCKED'
    );
};
